import { useState } from 'react';
import { ATLAS } from '../lib/foxhole-maps.js';
import { useNewWar } from '../lib/new-war-api.js';

export interface NewWarButtonProps {
  /** Number of map objects currently on the board (for confirm copy). */
  assetCount: number;
}

const ALL_REGION_KEYS: readonly string[] = ATLAS.map((t) => t.key);

/**
 * Admin-only "New war" reset. Wipes every map object and disables
 * every hex so the planner starts blank. Hidden behind a confirmation
 * modal because the action is irreversible.
 */
export function NewWarButton({ assetCount }: NewWarButtonProps): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const newWar = useNewWar();
  const busy = newWar.isPending;

  function onConfirm(): void {
    newWar.mutate(
      { disabledRegionKeys: [...ALL_REGION_KEYS] },
      {
        onSuccess: () => setConfirming(false),
      },
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="rounded bg-red-700 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-white hover:bg-red-600"
        title="Reset the map for a new war (admin only)"
      >
        New war
      </button>

      {confirming && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-war-title"
          onClick={() => {
            if (!busy) setConfirming(false);
          }}
        >
          <div
            className="w-full max-w-md rounded-lg border border-red-700 bg-slate-900 p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="new-war-title"
              className="mb-2 text-lg font-bold text-red-400"
            >
              Start a new war?
            </h2>
            <p className="mb-3 text-sm text-slate-200">
              This action is <strong className="text-red-300">irreversible</strong>. It will:
            </p>
            <ul className="mb-4 list-inside list-disc space-y-1 text-sm text-slate-300">
              <li>
                Delete <strong className="text-red-300">{assetCount}</strong> map asset
                {assetCount === 1 ? '' : 's'} (no exceptions).
              </li>
              <li>
                Disable <strong className="text-red-300">all {ALL_REGION_KEYS.length}</strong>{' '}
                hexes by default.
              </li>
            </ul>
            {newWar.isError && (
              <p className="mb-3 rounded bg-red-950/60 px-2 py-1 text-xs text-red-300">
                Failed to reset map. Please try again.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="rounded bg-slate-700 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={busy}
                className="rounded bg-red-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-50"
              >
                {busy ? 'Resetting…' : 'Yes, start new war'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
